/**
 * 안드로이드 하드웨어 뒤로 가기(DEV-12 · DEV_SPEC 3장 뒤로 가기 규칙).
 *
 * 앱 껍데기(android MainActivity)가 뒤로 가기를 받으면 `window.__insaengBack()` 을 부릅니다.
 * 규칙은 화면이 이미 알고 있어서(상단 바 「뒤로」가 1:1 → 별채, 고민 스레드 → 들어온 곳 …) 그 길을 그대로 탑니다.
 *
 *   1. 시트 · 대화 상자가 열려 있으면 닫기(「닫기」 · 「취소」 버튼, 없으면 Esc). 열린 동안엔 앱을 끄지 않음
 *   2. 상단 바 「뒤로」가 있으면 누르기(그 화면의 규칙대로)
 *   3. 탭 첫 화면(별채대화 · 상회칠판 · 나)과 시작 화면은 'exit' — 껍데기가 「한 번 더 누르면 종료」
 *   4. 그 밖은 브라우저 기록 한 걸음
 *
 * 웹뷰 기록(canGoBack)만 믿지 않습니다: 로그인 · 동의 화면으로 되돌아가거나, 자동으로 쌓인 기록을 건너뛰는 일이 있어서(히어로챗 QA).
 */

/** 두 번 눌러 끄는 첫 화면들 */
export const ROOT_PATHS = new Set(['/', '/board', '/me', '/start', '/manual-login', '/welcome', '/counter', '/goodbye']);

const OVERLAY_SELECTOR = '[aria-modal="true"], [role="dialog"], [role="alertdialog"], [role="menu"]';

function isVisible(element) {
    // 숨긴 탭 · 접힌 겹창 안의 것은 건너뜀
    return Boolean(element) && !element.closest('[hidden], [aria-hidden="true"]');
}

/** 맨 위(문서에서 마지막) 열린 겹창 */
function topOverlay(doc) {
    const overlays = [...doc.querySelectorAll(OVERLAY_SELECTOR)].filter(isVisible);
    return overlays.at(-1) ?? null;
}

function closeButtonOf(overlay) {
    const byLabel = overlay.querySelector('button[aria-label="닫기"]');
    if (byLabel) {
        return byLabel;
    }
    return (
        [...overlay.querySelectorAll('button')].find((button) => /^(취소|닫기)$/u.test(button.textContent.trim())) ??
        null
    );
}

/**
 * 뒤로 가기 한 번을 처리합니다.
 * @returns {'closed' | 'back' | 'history' | 'exit'}
 */
export function handleNativeBack({ doc = document, win = window } = {}) {
    const overlay = topOverlay(doc);
    if (overlay) {
        const button = closeButtonOf(overlay);
        if (button) {
            button.click();
        } else {
            doc.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
        }
        return 'closed';
    }
    const backButton = [...doc.querySelectorAll('header button[aria-label="뒤로"]')].find(isVisible);
    if (backButton) {
        backButton.click();
        return 'back';
    }
    if (ROOT_PATHS.has(win.location.pathname)) {
        return 'exit';
    }
    win.history.back();
    return 'history';
}

/** 껍데기가 부를 자리. 웹(브라우저)에서도 걸어 두지만 아무도 부르지 않습니다 */
export function installNativeBack(win = window) {
    win.__insaengBack = () => handleNativeBack({ doc: win.document, win });
}
